import React, { useEffect, useState } from "react";
import { X, Users, UserPlus, Trash2, Loader2, Shield, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export default function MembersModal({ open, onClose, usage, onUsageReset }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = () => api.listMembers().then(setData).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  if (!open) return null;

  const add = async () => {
    try {
      setBusy(true);
      await api.addMember(form);
      setForm({ name: "", email: "", password: "", role: "member" });
      load();
      toast.success("Member added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add member"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.removeMember(id); load(); toast.success("Member removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  const resetUsage = async () => {
    try { await api.resetUsage(); toast.success("Usage reset"); onUsageReset?.(); }
    catch (e) { toast.error("Reset failed"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="members-modal">
      <div className="bg-[#0B0F17] border border-white/10 rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5"><Users size={18} className="text-emerald-400" /><span className="font-head font-bold text-white">Company & team</span></div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-members-modal"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between bg-[#111827] border border-white/10 rounded-xl px-4 py-3">
            <div>
              <div className="text-sm text-white font-semibold">{data?.company?.name || user?.companyName}</div>
              <div className="text-[11px] text-slate-500">Plan: {usage?.tier || data?.company?.tier || "Free"} · You are {user?.role}</div>
            </div>
            {isAdmin && (
              <button data-testid="reset-usage-button" onClick={resetUsage} className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-emerald-300 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                <RotateCcw size={13} /> Reset usage
              </button>
            )}
          </div>

          {isAdmin && (
            <div className="bg-[#111827] border border-white/10 rounded-xl p-4">
              <div className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><UserPlus size={15} className="text-emerald-400" /> Add teammate</div>
              <div className="grid sm:grid-cols-2 gap-2">
                <input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200" />
                <input data-testid="member-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200" />
                <input data-testid="member-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temp password (min 6)" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200" />
                <select data-testid="member-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200">
                  <option value="member" className="bg-[#111827]">Member</option>
                  <option value="admin" className="bg-[#111827]">Admin</option>
                </select>
              </div>
              <button data-testid="add-member-button" onClick={add} disabled={busy} className="mt-2 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Add member
              </button>
            </div>
          )}

          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Members ({data?.members?.length || 0})</div>
            {(data?.members || []).map((m) => (
              <div key={m.id} data-testid="member-item" className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10">
                <span className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold">{(m.name || m.email)[0]?.toUpperCase()}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 truncate">{m.name} {m.id === user?.id && <span className="text-[10px] text-slate-500">(you)</span>}</div>
                  <div className="text-[11px] text-slate-500 truncate">{m.email}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-md border flex items-center gap-1 ${m.role === "admin" ? "bg-amber-500/10 text-amber-300 border-amber-500/20" : "bg-white/5 text-slate-400 border-white/10"}`}>
                  {m.role === "admin" && <Shield size={10} />}{m.role}
                </span>
                {isAdmin && m.id !== user?.id && (
                  <button data-testid="remove-member-button" onClick={() => remove(m.id)} className="text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
